import { useTheme } from 'styled-components';
import {
  LongArrowIcon,
  TableCell,
  Typography,
  useStatusInfo,
} from '@libs/components';
import { formatCryptoAmount } from '@libs/helpers';
import { formatDate } from '@libs/hooks';
import { TransactionStatusType, TransactionType } from '@libs/types';
import { TRANSACTION_LABELS, USDT_PRICE } from 'src/constants';
import { useAppDisclaimer, useTranslation } from 'src/hooks';
import { TransactionsGroupedByDate } from 'src/gql';
import {
  ArrowWrapper,
  CodeTypography,
  CustomAssetNetworkIcon,
  FlexCenter,
  MobileTableWrapper,
  MobileUserAssetRightComponentWrapper,
  MobileUserAssetWrapper,
  StickyContainer,
} from './styles';

type MobileTableProps = {
  transactions?: TransactionsGroupedByDate[];
  handleTransactionClick: (id: number) => void;
};

export const MobileTable: React.FC<MobileTableProps> = ({
  transactions,
  handleTransactionClick,
}) => {
  const { colors } = useTheme();
  const { getStatusData } = useStatusInfo();
  const { isAppDisclaimerVisible } = useAppDisclaimer();
  const { t } = useTranslation();

  return (
    <MobileTableWrapper>
      {transactions?.map((resp, index) => {
        const date = new Date(resp.createdAt);
        const formatedDate = formatDate('D MMM YYYY', date);
        return (
          <div key={index}>
            <StickyContainer isAppDisclaimerVisible={!!isAppDisclaimerVisible}>
              <Typography
                color={colors.text.secondary}
                type="bodyButton"
                fontFamily="primaryMedium"
              >
                {formatedDate}
              </Typography>
            </StickyContainer>
            {resp.transactions.map((transactions) => {
              const { assetCode: code, type, amount } = transactions.details;
              const assetCode = code;

              const amountToShow =
                type === TransactionType.BonusClaim
                  ? formatCryptoAmount(amount, USDT_PRICE, 2)
                  : amount;
              const transactionStatus =
                transactions.status === TransactionStatusType.DRAFT
                  ? getStatusData(TransactionStatusType.PENDING)
                  : getStatusData(transactions.status);

              return (
                <MobileUserAssetWrapper
                  isLastElement={resp.transactions.length === 1}
                  key={transactions.id}
                  onClick={handleTransactionClick.bind(null, transactions.id)}
                >
                  <TableCell
                    img={
                      <CustomAssetNetworkIcon
                        isAlone={!transactions.details.toAsset?.iconUrl}
                        baseUrl={
                          transactions.details.fromAsset?.iconUrl ??
                          transactions.details.assetIconUrl
                        }
                        networkIcon={transactions.details.toAsset?.iconUrl}
                      />
                    }
                    title={
                      <FlexCenter>
                        <Typography
                          type="subHeadline"
                          fontFamily="primaryMedium"
                        >
                          {transactions.details.fromAsset?.name
                            ? transactions.details.fromAsset.name
                            : transactions.details.assetLabel}
                        </Typography>
                        {!transactions.details.toAsset?.name && (
                          <CodeTypography
                            type="bodyButton"
                            fontFamily="primaryRegular"
                            color={colors.text.secondary}
                          >
                            {transactions.details.assetCode}
                          </CodeTypography>
                        )}
                        {transactions.details.toAsset?.name && (
                          <FlexCenter>
                            <ArrowWrapper>
                              <LongArrowIcon />
                            </ArrowWrapper>
                            <Typography
                              type="subHeadline"
                              fontFamily="primaryMedium"
                            >
                              {transactions.details.toAsset?.name}
                            </Typography>
                          </FlexCenter>
                        )}
                      </FlexCenter>
                    }
                    description={
                      <Typography
                        type="caption"
                        fontFamily="primaryRegular"
                        color={colors.text.secondary}
                      >
                        {t(TRANSACTION_LABELS[transactions.details.type])}
                      </Typography>
                    }
                  />
                  <MobileUserAssetRightComponentWrapper>
                    <TableCell
                      title={`${amountToShow} ${assetCode}`}
                      justifyToLeft={false}
                      description={t(transactionStatus.text)}
                      color={transactionStatus.colors[0]}
                    />
                  </MobileUserAssetRightComponentWrapper>
                </MobileUserAssetWrapper>
              );
            })}
          </div>
        );
      })}
    </MobileTableWrapper>
  );
};
